import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Backdrop,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    LinearProgress,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core";
import * as Yup from "yup";
import _ from "lodash";
import { alpha, makeStyles } from "@material-ui/core/styles";
import { Form, Formik, useFormikContext } from "formik";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import CropFreeIcon from "@material-ui/icons/CropFree";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import MeetingRoomOutlinedIcon from "@material-ui/icons/MeetingRoomOutlined";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import clsx from "classnames";
import SnackBar from "components/SnackBar";
import ContainerHeader from "components/ContainerHeader";
import CardBox from "components/CardBox";
import Canvas from "components/Canvas";
import FormField from "components/FormField";
import { getFieldValues } from "components/EZForm";
import ToolTipIcon from "components/ToolTipIcon";
import API from "util/Api";

const AUTO_REFRESH_MS = 60000;
const EMPTY_SLOTS = [];

const useStyles = makeStyles((theme) => ({
    headerStyle: {
        textAlign: "end",
        marginBottom: 0,
    },
    workspace: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    filterCard: {
        overflow: "hidden",
        borderRadius: 20,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        boxShadow: "0 14px 32px rgba(15, 23, 42, 0.06)",
        marginBottom: theme.spacing(3),
    },
    filterMeta: {
        padding: theme.spacing(1.5, 2.25, 0),
        color: alpha("#102a43", 0.74),
    },
    filterBar: {
        display: "grid",
        gridTemplateColumns: "minmax(220px, 1.25fr) minmax(180px, 1fr) auto",
        gap: theme.spacing(2),
        alignItems: "end",
        padding: theme.spacing(2, 2.25),
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr 1fr",
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            alignItems: "stretch",
        },
    },
    filterField: {
        "& .MuiFormControl-root": {
            marginTop: 0,
        },
    },
    filterActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(0.5),
        [theme.breakpoints.down("md")]: {
            gridColumn: "1 / -1",
            justifyContent: "flex-start",
            paddingBottom: 0,
        },
        [theme.breakpoints.down("sm")]: {
            flexWrap: "wrap",
        },
    },
    summaryStrip: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
        gap: theme.spacing(2),
    },
    statCard: {
        position: "relative",
        overflow: "hidden",
        minHeight: 112,
        padding: theme.spacing(2.25),
        borderRadius: 20,
        color: "#fff",
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.12)",
    },
    statCardSoft: {
        background: "linear-gradient(145deg, #0f172a, #1e293b)",
    },
    statCardSuccess: {
        background: "linear-gradient(145deg, #166534, #22c55e)",
    },
    statCardDanger: {
        background: "linear-gradient(145deg, #991b1b, #ef4444)",
    },
    statCardInfo: {
        background: "linear-gradient(145deg, #0f4c81, #38bdf8)",
    },
    statIcon: {
        opacity: 0.28,
        position: "absolute",
        right: theme.spacing(2),
        top: theme.spacing(2),
        fontSize: 34,
    },
    statLabel: {
        opacity: 0.8,
        fontSize: 12,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
    },
    statValue: {
        marginTop: theme.spacing(1),
        fontSize: 30,
        fontWeight: 700,
        lineHeight: 1.1,
    },
    statHelper: {
        marginTop: theme.spacing(1),
        opacity: 0.86,
        fontSize: 13,
    },
    mapShell: {
        overflow: "hidden",
        borderRadius: 24,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
    },
    mapToolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        padding: theme.spacing(2.25, 2.5),
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: alpha("#ffffff", 0.72),
        backdropFilter: "blur(10px)",
    },
    mapHeading: {
        fontWeight: 700,
        color: "#102a43",
    },
    mapSubheading: {
        marginTop: theme.spacing(0.5),
        color: alpha("#102a43", 0.74),
    },
    liveDot: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        display: "inline-block",
        marginRight: theme.spacing(1),
        background: "#22c55e",
        boxShadow: "0 0 0 6px rgba(34, 197, 94, 0.15)",
    },
    toolbarActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    toggleGroup: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 999,
        background: alpha(theme.palette.primary.main, 0.07),
    },
    statusTabs: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    statusTab: {
        border: 0,
        outline: 0,
        cursor: "pointer",
        borderRadius: 999,
        padding: theme.spacing(1, 1.75),
        background: "#fff",
        color: "#102a43",
        fontWeight: 600,
        transition: "all 0.2s ease",
        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.14)}`,
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.24)}`,
        },
    },
    statusTabActive: {
        color: "#fff",
        background: theme.palette.primary.main,
        boxShadow: "0 14px 30px rgba(25, 118, 210, 0.24)",
    },
    bodyGrid: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 320px",
        alignItems: "stretch",
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
            alignItems: "start",
        },
    },
    mapPane: {
        minWidth: 0,
    },
    canvasViewport: {
        position: "relative",
        minHeight: 540,
        overflow: "auto",
        padding: theme.spacing(2.5),
        background:
            "radial-gradient(circle at top, rgba(56, 189, 248, 0.12), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
        [theme.breakpoints.down("sm")]: {
            minHeight: 420,
            padding: theme.spacing(1.5),
        },
    },
    sidePanel: {
        display: "flex",
        borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: alpha("#fff", 0.84),
        backdropFilter: "blur(8px)",
        [theme.breakpoints.down("md")]: {
            display: "block",
            borderLeft: 0,
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        },
    },
    sidePanelInner: {
        flex: 1,
        minHeight: "100%",
        padding: theme.spacing(2.5),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    sidePanelTitle: {
        fontWeight: 700,
        color: "#102a43",
    },
    sidePanelText: {
        color: alpha("#102a43", 0.74),
    },
    utilizationBlock: {
        padding: theme.spacing(2),
        borderRadius: 18,
        background: "linear-gradient(145deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.92))",
        color: "#fff",
    },
    utilizationMeter: {
        height: 10,
        marginTop: theme.spacing(1.5),
        borderRadius: 999,
        background: alpha("#fff", 0.18),
        overflow: "hidden",
    },
    utilizationFill: {
        height: "100%",
        borderRadius: 999,
        background: "linear-gradient(90deg, #38bdf8 0%, #22c55e 52%, #f97316 100%)",
        transition: "width 0.25s ease",
    },
    slotList: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        overflowY: "auto",
        paddingRight: theme.spacing(0.5),
    },
    slotButton: {
        width: "100%",
        border: 0,
        outline: 0,
        cursor: "pointer",
        textAlign: "left",
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 16,
        background: "#fff",
        color: "#102a43",
        transition: "all 0.2s ease",
        boxShadow: `inset 0 0 0 1px ${alpha("#102a43", 0.08)}`,
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        },
    },
    slotButtonSelected: {
        background: alpha(theme.palette.primary.main, 0.08),
        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.32)}`,
    },
    slotButtonTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
    slotBadge: {
        minWidth: 84,
        textAlign: "center",
        padding: theme.spacing(0.5, 1),
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
    },
    slotBadgeAvailable: {
        background: alpha("#22c55e", 0.16),
        color: "#166534",
    },
    slotBadgeOccupied: {
        background: alpha("#ef4444", 0.14),
        color: "#b91c1c",
    },
    slotMeta: {
        marginTop: theme.spacing(0.75),
        fontSize: 12,
        color: alpha("#102a43", 0.68),
    },
    slotSearch: {
        "& .MuiOutlinedInput-root": {
            background: "#fff",
            borderRadius: 12,
        },
    },
    emptyState: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 260,
        borderRadius: 20,
        border: `1px dashed ${alpha(theme.palette.primary.main, 0.18)}`,
        color: alpha("#102a43", 0.7),
        background: alpha("#fff", 0.52),
        padding: theme.spacing(3),
        textAlign: "center",
    },
}));

const STATUS_FILTERS = [
    { key: "all", label: "All Slots" },
    { key: "available", label: "Available" },
    { key: "occupied", label: "Occupied" },
];

const FormSync = ({ handleChange }) => {
    const formik = useFormikContext();

    useEffect(() => {
        if (handleChange) {
            handleChange(formik);
        }
    }, [formik.values, handleChange]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

const ParkingSlots = (props) => {
    const handle = useFullScreenHandle();
    const classes = useStyles();
    const canvasViewportRef = useRef(null);
    const [sidePanelHeight, setSidePanelHeight] = useState(null);
    const [fields, setFields] = useState();
    const [floor, setFloor] = useState();
    const [scale, setScale] = useState(1);
    const [fullScreen, setFullScreen] = useState(false);
    const [formData, setFormData] = useState();
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [blinkAvailable, setBlinkAvailable] = useState(false);
    const [showLabels, setShowLabels] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedSlotNo, setSelectedSlotNo] = useState(null);
    const [slotSearch, setSlotSearch] = useState("");
    const [snackBar, setSnackBar] = useState({
        open: false,
        message: "",
        type: "error",
    });
    const refreshTimeoutRef = useRef(null);

    const validations = Yup.object().shape({
        location: Yup.object().required("Required"),
    });
    const formFields = fields ? Object.values(fields) : EMPTY_SLOTS;
    const initialValues = fields ? getFieldValues(formFields) : null;

    const syncFloorData = async (data, { silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
        }

        try {
            const res = await API.post("parkingFloorMap", JSON.stringify(data));
            setFloor(res.data.data);
            setFormData(data);
            setLastUpdated(new Date());
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    const handleSubmit = async (data, { setSubmitting }) => {
        setFloor(undefined);
        setSelectedSlotNo(null);

        try {
            await syncFloorData(data);
        } catch (err) {
            setSnackBar({
                open: true,
                message: err?.response?.data?.msg ?? "Something went wrong",
                type: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRefresh = async () => {
        if (!formData) return;

        try {
            await syncFloorData(formData, { silent: true });
        } catch (err) {
            setSnackBar({
                open: true,
                message: err?.response?.data?.msg ?? "Refresh failed",
                type: "error",
            });
        }
    };

    const handleChange = (formik) => {
        const values = formik.values;
        if (!fields || !values?.location?.floors) return;
        if (!_.isEqual(values.location.floors, fields?.floor?.options)) {
            formik.setFieldValue("floor_id", "");
            const config = _.cloneDeep(fields);
            if (config?.floor) config.floor.options = values.location.floors;
            setFields(config);
        }
    };

    useEffect(() => {
        API.get("parkingFloorMap")
            .then((res) => {
                setFields(res.data.data.fields);
            })
            .catch((err) => {
                console.error("error", err);
            });
    }, []);

    useEffect(() => {
        if (!formData) return undefined;

        let cancelled = false;

        const tick = async () => {
            try {
                const res = await API.post("parkingFloorMap", JSON.stringify(formData));
                if (!cancelled) {
                    setFloor(res.data.data);
                    setLastUpdated(new Date());
                }
            } catch (err) {
                if (!cancelled) {
                    setSnackBar({
                        open: true,
                        message: err?.response?.data?.msg ?? "Refresh failed",
                        type: "error",
                    });
                }
            } finally {
                if (!cancelled) {
                    refreshTimeoutRef.current = setTimeout(tick, AUTO_REFRESH_MS);
                }
            }
        };

        refreshTimeoutRef.current = setTimeout(tick, AUTO_REFRESH_MS);

        return () => {
            cancelled = true;
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        };
    }, [formData]);

    const slots = floor?.slots ?? EMPTY_SLOTS;
    const availableCount = slots.reduce(
        (acc, slot) => acc + (Number(slot.is_available) === 1 ? 1 : 0),
        0
    );
    const occupiedCount = slots.length - availableCount;
    const occupancyRate = slots.length ? Math.round((occupiedCount / slots.length) * 100) : 0;

    const filteredSlots = useMemo(() => {
        return slots.filter((slot) => {
            if (statusFilter === "available") return Number(slot.is_available) === 1;
            if (statusFilter === "occupied") return Number(slot.is_available) !== 1;
            return true;
        });
    }, [slots, statusFilter]);

    const searchedSlots = useMemo(() => {
        const query = slotSearch.trim().toLowerCase();
        if (!query) return filteredSlots;

        return filteredSlots.filter((slot) => {
            const slotNo = String(slot.slot_no ?? "").toLowerCase();
            const vehicleNo = String(slot.vehicle_no ?? "").toLowerCase();
            return slotNo.includes(query) || vehicleNo.includes(query);
        });
    }, [filteredSlots, slotSearch]);

    const selectedSlot = useMemo(() => {
        return slots.find((slot) => slot.slot_no === selectedSlotNo) || null;
    }, [slots, selectedSlotNo]);

    useEffect(() => {
        if (!selectedSlotNo) return;
        if (!searchedSlots.some((slot) => slot.slot_no === selectedSlotNo)) {
            setSelectedSlotNo(null);
        }
    }, [searchedSlots, selectedSlotNo]);

    useEffect(() => {
        const node = canvasViewportRef.current;
        if (!node) return undefined;

        const updateHeight = () => {
            if (window.innerWidth <= 960) {
                setSidePanelHeight(null);
                return;
            }
            setSidePanelHeight(node.clientHeight || null);
        };

        updateHeight();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateHeight);
            return () => window.removeEventListener("resize", updateHeight);
        }

        const observer = new ResizeObserver(() => {
            updateHeight();
        });

        observer.observe(node);
        window.addEventListener("resize", updateHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateHeight);
        };
    }, [floor, fullScreen, scale]);

    return (
        <>
            <ContainerHeader title={"Parking Map Workspace"} match={props.match} />
            {initialValues && (
                <div className="row">
                    <div className="col-12">
                        <div className={classes.filterCard}>
                            {/* <Typography variant="body2" className={classes.filterMeta}>
                                Pick a location and floor, then load the live parking map.
                            </Typography> */}
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validations}
                                onSubmit={handleSubmit}
                                enableReinitialize
                            >
                                {(formik) => (
                                    <Form>
                                        <div className={classes.filterBar}>
                                            {formFields.map((field) => (
                                                <div
                                                    key={`parking-filter-${field.key}`}
                                                    className={classes.filterField}
                                                >
                                                    <FormField field={{ ...field, name: field.key }} />
                                                </div>
                                            ))}
                                            <div className={classes.filterActions}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    type="submit"
                                                    disabled={formik.isSubmitting}
                                                >
                                                    View Map
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    type="button"
                                                    disabled={formik.isSubmitting}
                                                    onClick={() => {
                                                        formik.resetForm();
                                                        setFloor(undefined);
                                                        setSelectedSlotNo(null);
                                                        setFormData(undefined);
                                                        setLastUpdated(null);
                                                    }}
                                                >
                                                    Reset
                                                </Button>
                                            </div>
                                        </div>
                                        <FormSync handleChange={handleChange} />
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            )}

            <div className="row" style={{ display: floor ? "block" : "none" }}>
                <Backdrop open={loading} style={{ zIndex: 1301, color: "#fff" }}>
                    <CircularProgress color="primary" />
                </Backdrop>

                <CardBox
                    styleName="col-12"
                    headingStyle={classes.headerStyle}
                    heading=""
                >
                    <div className={classes.workspace}>
                        <div className={classes.summaryStrip}>
                            <div className={clsx(classes.statCard, classes.statCardInfo)}>
                                <MeetingRoomOutlinedIcon className={classes.statIcon} />
                                <div className={classes.statLabel}>Total Slots</div>
                                <div className={classes.statValue}>{slots.length}</div>
                                <div className={classes.statHelper}>
                                    {searchedSlots.length} visible with current filter
                                </div>
                            </div>
                            <div className={clsx(classes.statCard, classes.statCardSuccess)}>
                                <RoomOutlinedIcon className={classes.statIcon} />
                                <div className={classes.statLabel}>Available</div>
                                <div className={classes.statValue}>{availableCount}</div>
                                <div className={classes.statHelper}>Ready for incoming vehicles</div>
                            </div>
                            <div className={clsx(classes.statCard, classes.statCardDanger)}>
                                <VisibilityOutlinedIcon className={classes.statIcon} />
                                <div className={classes.statLabel}>Occupied</div>
                                <div className={classes.statValue}>{occupiedCount}</div>
                                <div className={classes.statHelper}>{occupancyRate}% occupancy right now</div>
                            </div>
                        </div>

                        <div className={classes.mapShell}>
                            <div className={classes.mapToolbar}>
                                <div>
                                    <Typography variant="h6" className={classes.mapHeading}>
                                        {floor?.location} - Floor {floor?.floor_no}
                                    </Typography>
                                    <Typography variant="body2" className={classes.mapSubheading}>
                                        <span className={classes.liveDot} />
                                        {lastUpdated
                                            ? `Last synced at ${lastUpdated.toLocaleTimeString([], {
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                              })}`
                                            : "Waiting for first refresh"}
                                    </Typography>
                                </div>

                                <div className={classes.toolbarActions}>
                                    <div className={classes.toggleGroup}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="primary"
                                                    checked={showLabels}
                                                    onChange={(e) => setShowLabels(e.target.checked)}
                                                />
                                            }
                                            label="Labels"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="primary"
                                                    checked={blinkAvailable}
                                                    onChange={(e) => setBlinkAvailable(e.target.checked)}
                                                />
                                            }
                                            label="Blink Free Slots"
                                        />
                                    </div>

                                    <ToolTipIcon
                                        title="Refresh"
                                        ariaLabel="refresh"
                                        icon={<RefreshIcon fontSize="large" />}
                                        color="primary"
                                        onClick={handleRefresh}
                                        disabled={!formData}
                                    />
                                    {document.fullscreenEnabled && (
                                        <ToolTipIcon
                                            title="Full Screen"
                                            ariaLabel="full-screen"
                                            icon={<CropFreeIcon fontSize="large" />}
                                            color="primary"
                                            onClick={() => {
                                                if (!handle.active) {
                                                    handle.enter();
                                                } else {
                                                    handle.exit();
                                                }
                                            }}
                                        />
                                    )}
                                    <ToolTipIcon
                                        title="Zoom In"
                                        ariaLabel="zoom-in"
                                        icon={<ZoomInIcon fontSize="large" />}
                                        color="primary"
                                        onClick={() => {
                                            setScale((prevScale) => Math.min(prevScale + 0.2, 15));
                                        }}
                                    />
                                    <ToolTipIcon
                                        title="Zoom Out"
                                        ariaLabel="zoom-out"
                                        icon={<ZoomOutIcon fontSize="large" />}
                                        color="primary"
                                        onClick={() => {
                                            setScale((prevScale) => Math.max(prevScale - 0.2, 1));
                                        }}
                                    />
                                    <ToolTipIcon
                                        title="Reset View"
                                        ariaLabel="reset-view"
                                        icon={<FilterListIcon fontSize="large" />}
                                        color="primary"
                                        onClick={() => {
                                            setScale(1);
                                            setSelectedSlotNo(null);
                                        }}
                                    />
                                </div>
                            </div>

                            {loading && <LinearProgress color="primary" />}

                            <div style={{ padding: 20 }}>
                                <div className={classes.statusTabs}>
                                    {STATUS_FILTERS.map((filter) => (
                                        <button
                                            key={filter.key}
                                            type="button"
                                            className={clsx(classes.statusTab, {
                                                [classes.statusTabActive]: statusFilter === filter.key,
                                            })}
                                            onClick={() => setStatusFilter(filter.key)}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <Divider />

                            <FullScreen handle={handle} onChange={setFullScreen}>
                                <div className={classes.bodyGrid}>
                                    <div className={classes.mapPane}>
                                        <div ref={canvasViewportRef} className={classes.canvasViewport}>
                                            <Canvas
                                                floor={floor}
                                                scale={scale}
                                                blink={blinkAvailable}
                                                fullScreen={fullScreen}
                                                showAvailabilityTooltip={true}
                                                showLabels={showLabels}
                                                statusFilter={statusFilter}
                                                selectedSlotNo={selectedSlotNo}
                                                onSlotSelect={(slot) => setSelectedSlotNo(slot?.slot_no ?? null)}
                                            />
                                        </div>
                                    </div>

                                    <aside className={classes.sidePanel}>
                                        <div
                                            className={classes.sidePanelInner}
                                            style={sidePanelHeight ? { height: sidePanelHeight } : undefined}
                                        >
                                            <div>
                                                <Typography variant="subtitle1" className={classes.sidePanelTitle}>
                                                    Floor Snapshot
                                                </Typography>
                                                <Typography variant="body2" className={classes.sidePanelText}>
                                                    Select a slot from the list or click the map to focus it.
                                                </Typography>
                                            </div>

                                            <div className={classes.utilizationBlock}>
                                                <Typography variant="body2">
                                                    Occupancy is currently {occupancyRate}% across this floor.
                                                </Typography>
                                                <div className={classes.utilizationMeter}>
                                                    <div
                                                        className={classes.utilizationFill}
                                                        style={{ width: `${occupancyRate}%` }}
                                                    />
                                                </div>
                                                <Typography
                                                    variant="caption"
                                                    style={{ display: "block", marginTop: 12, opacity: 0.8 }}
                                                >
                                                    {occupiedCount} occupied / {slots.length} total slots
                                                </Typography>
                                            </div>

                                            {selectedSlot && (
                                                <div>
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={classes.sidePanelTitle}
                                                    >
                                                        Selected Slot
                                                    </Typography>
                                                    <Typography variant="body2" className={classes.sidePanelText}>
                                                        Slot {selectedSlot.slot_no} is{" "}
                                                        {Number(selectedSlot.is_available) === 1
                                                            ? "available"
                                                            : "occupied"}
                                                        {Number(selectedSlot.is_available) === 1
                                                            ? "."
                                                            : ` by ${selectedSlot.vehicle_no || "unknown vehicle"}.`}
                                                    </Typography>
                                                </div>
                                            )}

                                            <div>
                                                <Typography variant="subtitle2" className={classes.sidePanelTitle}>
                                                    Visible Slots
                                                </Typography>
                                                <Typography variant="body2" className={classes.sidePanelText}>
                                                    {searchedSlots.length} slot
                                                    {searchedSlots.length === 1 ? "" : "s"} match the current
                                                    filter.
                                                </Typography>
                                            </div>

                                            <TextField
                                                value={slotSearch}
                                                onChange={(e) => setSlotSearch(e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Search slot or vehicle no"
                                                className={classes.slotSearch}
                                                fullWidth
                                            />

                                            {searchedSlots.length > 0 ? (
                                                <div className={classes.slotList}>
                                                    {searchedSlots.map((slot) => {
                                                        const isAvailable = Number(slot.is_available) === 1;
                                                        const isSelected = slot.slot_no === selectedSlotNo;

                                                        return (
                                                            <button
                                                                key={slot.slot_no}
                                                                type="button"
                                                                className={clsx(classes.slotButton, {
                                                                    [classes.slotButtonSelected]: isSelected,
                                                                })}
                                                                onClick={() => setSelectedSlotNo(slot.slot_no)}
                                                            >
                                                                <div className={classes.slotButtonTop}>
                                                                    <Typography variant="subtitle2">
                                                                        Slot {slot.slot_no}
                                                                    </Typography>
                                                                    <span
                                                                        className={clsx(classes.slotBadge, {
                                                                            [classes.slotBadgeAvailable]: isAvailable,
                                                                            [classes.slotBadgeOccupied]:
                                                                                !isAvailable,
                                                                        })}
                                                                    >
                                                                        {isAvailable ? "Available" : "Occupied"}
                                                                    </span>
                                                                </div>
                                                                <div className={classes.slotMeta}>
                                                                    {isAvailable
                                                                        ? "Click to highlight on the floor map."
                                                                        : `Vehicle: ${slot.vehicle_no || "N/A"}`}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className={classes.emptyState}>
                                                    No slots match this filter or search on the selected floor.
                                                </div>
                                            )}
                                        </div>
                                    </aside>
                                </div>
                            </FullScreen>
                        </div>
                    </div>
                </CardBox>
            </div>

            <SnackBar
                {...snackBar}
                handleClose={() => {
                    setSnackBar((prev) => ({ ...prev, open: false, message: "" }));
                }}
            />
        </>
    );
};

export default ParkingSlots;
